import { useEffect, useState } from 'react';

/**
 * Hook to track document visibility state
 * Useful for pausing/resuming operations when tab is hidden/visible
 */
export const useDocumentVisibility = () => {
  const [isVisible, setIsVisible] = useState(() => {
    if (typeof document === 'undefined') return true;
    return document.visibilityState === 'visible';
  });

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const handleVisibilityChange = () => {
      const visible = document.visibilityState === 'visible';
      setIsVisible(visible);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return isVisible;
};

/**
 * Hook to execute callback when tab becomes visible
 */
export const useOnTabVisible = (callback: () => void) => {
  const isVisible = useDocumentVisibility();

  useEffect(() => {
    if (isVisible) {
      callback();
    }
  }, [isVisible, callback]);
};

/**
 * Hook to warn users before closing/refreshing page
 */
export const useBeforeUnload = (enabled: boolean, message?: string) => {
  useEffect(() => {
    if (!enabled) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = message || '';
      return message || '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled, message]);
};
